import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import {
  ExcludedKeyword,
  GeneratedQuery,
  Keyword,
  ThemeQueryGenerationRequest,
} from '@/types/themeQueryGenerationRequest';

// src/components/theme/edit/theme-setting-support-modal/ 配下のファイルで使用される共通関数を定義
export const useThemeSettingSupportModal = () => {
  const route = useRoute();
  const themeId = ref(Number(route.params.themeId ?? 0));

  watch(
    () => route.params.themeId,
    () => {
      themeId.value = Number(route.params.themeId ?? 0);
    },
  );

  /**
   * AIによる検索クエリ生成をトラッキングする関数
   */
  const trackAIKeywordsRequest = async (
    themeQueryGenerationRequest: ThemeQueryGenerationRequest,
  ) => {
    const filteredKeywordsJa =
      filterKeywordDatasetByDeletedKeywordsAndDuplications({
        keywordDataset: themeQueryGenerationRequest.generated_query.keywords,
        // NOTE: キーワード削除は計測には反映しない
        deletedKeywordUuidsSet: new Set(),
        lang: 'ja',
      });
    const filteredKeywordsEn =
      filterKeywordDatasetByDeletedKeywordsAndDuplications({
        keywordDataset: themeQueryGenerationRequest.generated_query.keywords,
        // NOTE: キーワード削除は計測には反映しない
        deletedKeywordUuidsSet: new Set(),
        lang: 'en',
      });
    await api.trackThemeEvent('ai_keywords_request', {
      pageName: 'theme_setting',
      pageUrl: route.fullPath,
      ...(themeId.value ? { theme: { id: themeId.value } } : {}),
      from: 'ai_generator',
      prompt: themeQueryGenerationRequest.request_text,
      keywords: [
        generateQueryFromKeywords(filteredKeywordsJa, 'ja'),
        generateQueryFromKeywords(filteredKeywordsEn, 'en'),
      ],
    });
  };

  const createDeletedKeywordUuid = (uuid: string, lang: 'ja' | 'en') => {
    return `${uuid}-${lang}`;
  };

  /**
   * `uuid` をキーにした `Map` を作成
   */
  const createUuidMap = ({
    keywordDataset,
    excludedKeywords,
  }: {
    keywordDataset: GeneratedQuery['keywords'];
    excludedKeywords: GeneratedQuery['excluded_keywords'];
  }): Map<string, Keyword | ExcludedKeyword> => {
    const uuidMap = new Map<string, Keyword | ExcludedKeyword>();
    keywordDataset.flat().forEach(obj => {
      if (obj.uuid) uuidMap.set(obj.uuid, obj);
    });
    excludedKeywords.forEach(obj => {
      if (obj.uuid) uuidMap.set(obj.uuid, obj);
    });
    return uuidMap;
  };

  const filterKeywordDatasetByDeletedKeywordsAndDuplications = ({
    keywordDataset,
    deletedKeywordUuidsSet,
    lang,
  }: {
    keywordDataset: GeneratedQuery['keywords'];
    deletedKeywordUuidsSet: Set<string>;
    lang: 'ja' | 'en';
  }) => {
    return keywordDataset
      .map(keywords => {
        const deletedKeywordsSet = new Set(
          keywords
            .filter(keyword =>
              deletedKeywordUuidsSet.has(
                createDeletedKeywordUuid(keyword.uuid, lang),
              ),
            )
            .map(keyword => keyword[lang]),
        );
        const uniqueKeywords = [
          ...new Map(keywords.map(item => [item[lang], item])).values(),
        ];
        return uniqueKeywords.filter(
          keyword => !deletedKeywordsSet.has(keyword[lang]),
        );
      })
      .filter(item => !(Array.isArray(item) && item.length === 0));
  };

  const filterExcludedKeywordsByDeletedKeywords = ({
    excludedKeywords,
    deletedKeywordUuidsSet,
    lang,
  }: {
    excludedKeywords: GeneratedQuery['excluded_keywords'];
    deletedKeywordUuidsSet: Set<string>;
    lang: 'ja' | 'en';
  }) => {
    return excludedKeywords.filter(
      excludedKeyword =>
        !deletedKeywordUuidsSet.has(
          createDeletedKeywordUuid(excludedKeyword.uuid, lang),
        ),
    );
  };

  const wrapIfSpaceExists = (str: string): string => {
    return str.includes(' ') ? `"${str}"` : str;
  };

  /**
   * キーワードデータセットからクエリを生成する関数。
   * 各キーワードセットは OR で結合され、セット間は AND で結合される。
   *
   * @param {GeneratedQuery['keywords']} keywordDataset - キーワードデータセット
   * @param {'ja' | 'en'} lang - 使用する言語 ('ja' または 'en')
   * @returns {string} 生成されたクエリ
   */
  const generateQueryFromKeywords = (
    keywordDataset: GeneratedQuery['keywords'],
    lang: 'ja' | 'en',
  ): string => {
    return keywordDataset
      .map(keywords => {
        const keywordQuery = keywords
          .map(keyword => `${wrapIfSpaceExists(keyword[lang])}`)
          .join(' OR '); // 内側は OR
        return `(${keywordQuery})`; // 外側も括弧で囲む
      })
      .join(' AND '); // 外側は AND
  };

  /**
   * 除外キーワードからクエリを生成する関数。
   * 各キーワードはスペースで結合され、先頭にマイナス記号が付与される。
   *
   * @param {GeneratedQuery['excluded_keywords']} excludedKeywords - 除外キーワード
   * @param {'ja' | 'en'} lang - 使用する言語 ('ja' または 'en')
   * @returns {string} 生成されたクエリ
   */
  const generateQueryFromExcludedKeywords = (
    excludedKeywords: GeneratedQuery['excluded_keywords'],
    lang: 'ja' | 'en',
  ): string => {
    return excludedKeywords
      .map(excludedKeyword => {
        return `-${wrapIfSpaceExists(excludedKeyword[lang])}`;
      })
      .join(' ');
  };

  return {
    createUuidMap,
    createDeletedKeywordUuid,
    filterKeywordDatasetByDeletedKeywordsAndDuplications,
    filterExcludedKeywordsByDeletedKeywords,
    wrapIfSpaceExists,
    generateQueryFromKeywords,
    generateQueryFromExcludedKeywords,
    trackAIKeywordsRequest,
  };
};
